/**
 * ProjectCard Component
 * 
 * Displays individual project information in a card format.
 * Shows project title, overview, images, and action buttons.
 * 
 * Features:
 * - Display project title and overview
 * - Show project images in a carousel/gallery
 * - Edit and delete buttons
 * - Image descriptions
 * - Display created and updated dates
 */

import React, { useState, useEffect } from 'react';
import ProjectImageTile from './ProjectImageTile.jsx';
import ProjectSkillsUsed from './ProjectSkillsUsed.jsx';

const normalizeProjectUrl = (value = '') => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) {
    return '';
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed}`;
};

const ProjectCard = ({ project, userId, onEdit, onDelete }) => {
  const [images, setImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  

  // Load project images from Firebase
  useEffect(() => {
    setImages(Array.isArray(project.images) ? project.images : []);
    setCurrentImageIndex(0);
  }, [project.images]);

  /**
   * Handle next image in carousel
   */
  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  /**
   * Handle previous image in carousel
   */
  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  /**
   * Format date for display
   */
  const formatDate = (date) => {
    if (!date) return '';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const currentImage = images[currentImageIndex];
  const projectUrl = normalizeProjectUrl(project?.projectUrl || project?.linkUrl || '');
  const createdDate = formatDate(project.createdAt);
  const updatedDate = formatDate(project.updatedAt);

  return (
    <article className="card project-management-card h-100">
      <div className="project-management-card-media">
        {currentImage ? (
          <ProjectImageTile imageId={currentImage.imageId} />
        ) : (
          <div className="project-management-card-placeholder" aria-hidden="true">
            <i className="bi bi-kanban"></i>
          </div>
        )}

        {images.length > 1 ? (
          <>
            <button className="project-card-image-control is-previous" onClick={handlePrevImage} aria-label="Previous project image">
              <i className="bi bi-chevron-left" aria-hidden="true"></i>
            </button>
            <button className="project-card-image-control is-next" onClick={handleNextImage} aria-label="Next project image">
              <i className="bi bi-chevron-right" aria-hidden="true"></i>
            </button>
            <span className="project-card-image-count">{currentImageIndex + 1} / {images.length}</span>
          </>
        ) : null}
      </div>

      <div className="card-body project-management-card-body">
        <div className="d-flex align-items-start gap-2">
          <h3 className="project-management-card-title flex-grow-1" title={project.title}>{project.title}</h3>
          <div className="btn-group btn-group-sm project-management-card-actions" role="group" aria-label={`Actions for ${project.title}`}>
          <button
            className="btn btn-outline-primary"
            onClick={onEdit}
            title={`Edit ${project.title}`}
            aria-label={`Edit ${project.title}`}
          >
            <i className="bi bi-pencil" aria-hidden="true"></i>
          </button>
          <button
            className="btn btn-outline-danger"
            onClick={onDelete}
            title={`Delete ${project.title}`}
            aria-label={`Delete ${project.title}`}
          >
            <i className="bi bi-trash" aria-hidden="true"></i>
          </button>
        </div>
      </div>
        <p className="project-management-card-overview text-muted" title={project.overview}>
          {project.overview || 'No project overview has been added yet.'}
        </p>
        <ProjectSkillsUsed userId={userId} skillIds={project.skillsUsed} className="project-management-card-skills" maxVisible={3} />
      </div>

      <div className="card-footer project-management-card-footer">
        <small className="text-muted text-truncate">
          <i className="bi bi-clock me-1" aria-hidden="true"></i>
          {updatedDate ? `Updated ${updatedDate}` : createdDate ? `Created ${createdDate}` : 'Date not available'}
        </small>
        {projectUrl ? (
          <a className="project-management-card-link" href={projectUrl} target="_blank" rel="noopener noreferrer">
            View project <i className="bi bi-arrow-up-right" aria-hidden="true"></i>
          </a>
        ) : null}
      </div>
    </article>
  );
};

export default ProjectCard;
